import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import classNames from 'classnames';

// Import configs and util modules
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { createResourceLocatorString } from '../../util/routes';
import { getWishlist, isInWishlist } from '../../util/wishlist';

// Import global thunk functions
import { toggleWishlist } from '../../ducks/wishlist.duck';

import css from './WishlistToggle.module.css';

/**
 * Heart icon. Filled when the listing is saved.
 */
export const IconHeart = ({ className, filled }) => (
  <svg
    className={className}
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill={filled ? 'currentColor' : 'none'}
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M12 20.5s-7.5-4.6-9.4-9.3C1.3 8 3.3 4.5 6.8 4.5c2.1 0 3.6 1.2 5.2 3 1.6-1.8 3.1-3 5.2-3 3.5 0 5.5 3.5 4.2 6.7-1.9 4.7-9.4 9.3-9.4 9.3z" />
  </svg>
);

/**
 * GardenPlace wishlist: save a listing to (or remove it from) the current user's wishlist.
 * Visitors who aren't logged in are sent to log in first. Not shown on the user's own listings.
 *
 * @component
 * @param {Object} props
 * @param {Object} props.listing listing entity (with author)
 * @param {'icon'|'button'} [props.variant='icon'] round heart (e.g. on listing cards) or a text button
 * @param {string} [props.authorId] uuid of the listing's author, if the listing doesn't include it
 * @param {string} [props.className]
 * @returns {JSX.Element|null}
 */
const WishlistToggle = props => {
  const { listing, variant = 'icon', className, authorId } = props;
  const intl = useIntl();
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const routeConfiguration = useRouteConfiguration();
  const currentUser = useSelector(state => state.user.currentUser);
  const savingListingIds = useSelector(state => state.wishlist.savingListingIds);

  // The wishlist depends on the logged in user, so the button is shown only in the browser
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
  }, []);

  const listingId = listing?.id?.uuid;
  const isOwnListing =
    !!currentUser?.id?.uuid && (listing?.author?.id?.uuid || authorId) === currentUser.id.uuid;
  if (!mounted || !listingId || isOwnListing) {
    return null;
  }

  const isSaved = isInWishlist(getWishlist(currentUser), listingId);
  const isSaving = savingListingIds.includes(listingId);
  const title = listing.attributes?.title || '';
  const label = intl.formatMessage(
    { id: isSaved ? 'WishlistToggle.removeLabel' : 'WishlistToggle.addLabel' },
    { title }
  );

  const handleClick = e => {
    // The button can be inside a link (listing cards)
    e.preventDefault();
    e.stopPropagation();
    if (!currentUser) {
      const state = { from: `${location.pathname}${location.search}${location.hash}` };
      history.push(createResourceLocatorString('LoginPage', routeConfiguration, {}, {}), state);
      return;
    }
    if (!isSaving) {
      dispatch(toggleWishlist(listing)).catch(() => null);
    }
  };

  return variant === 'button' ? (
    <button
      type="button"
      className={classNames(css.textButton, { [css.saved]: isSaved }, className)}
      onClick={handleClick}
      aria-pressed={isSaved}
      aria-label={label}
      disabled={isSaving}
    >
      <IconHeart className={css.textButtonIcon} filled={isSaved} />
      <FormattedMessage id={isSaved ? 'WishlistToggle.saved' : 'WishlistToggle.save'} />
    </button>
  ) : (
    <button
      type="button"
      className={classNames(css.iconButton, { [css.saved]: isSaved }, className)}
      onClick={handleClick}
      aria-pressed={isSaved}
      aria-label={label}
      title={label}
      disabled={isSaving}
    >
      <IconHeart className={css.icon} filled={isSaved} />
    </button>
  );
};

export default WishlistToggle;
